/**
 * Compass Permission Service for FlowRide
 * Manages device orientation and motion permission state and monitoring
 */

/**
 * Compass permission state type
 */
export type CompassPermissionState = "unknown" | "granted" | "denied" | "not_required";

/**
 * Permission state listener callback type
 */
type PermissionStateListener = ( state: CompassPermissionState ) => void;

/**
 * Service that manages compass permission state
 */
export class CompassPermissionService {

    /** Singleton instance */
    private static instance: CompassPermissionService | null = null;

    /** Permission state listeners */
    private permissionListeners: Set<PermissionStateListener> = new Set();

    /** Current permission state */
    private permissionState: CompassPermissionState = "unknown";

    /**
     * Private constructor for singleton pattern
     */
    private constructor() {
        // Private constructor
    }

    /**
     * Get singleton instance
     * @returns Service instance
     */
    public static getInstance(): CompassPermissionService {
        if ( CompassPermissionService.instance === null ) {
            CompassPermissionService.instance = new CompassPermissionService();
        }

        return CompassPermissionService.instance;
    }

    /**
     * Check if permission API is available
     * @returns True if permission API exists
     */
    private checkPermissionApiAvailable(): {
        "hasOrientationAPI": boolean;
        "hasMotionAPI":      boolean;
    } {
        const hasOrientationAPI =
            typeof DeviceOrientationEvent !== "undefined" &&
            typeof ( DeviceOrientationEvent as unknown as { "requestPermission"?: () => Promise<string> } ).requestPermission === "function";

        const hasMotionAPI =
            typeof DeviceMotionEvent !== "undefined" &&
            typeof ( DeviceMotionEvent as unknown as { "requestPermission"?: () => Promise<string> } ).requestPermission === "function";

        return {
            hasOrientationAPI,
            hasMotionAPI,
        };
    }

    /**
     * Whether the user agent is likely iOS Safari (requires gesture-gated requestPermission).
     * @returns True on iPhone / iPad / iPod user agents.
     */
    private isLikelyIOS(): boolean {
        if ( typeof navigator === "undefined" ) {
            return false;
        }

        // iPadOS 13+ may expose a macOS user agent; detect it through touch support.
        return ( /iPad|iPhone|iPod/ ).test( navigator.userAgent ) ||
          ( navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1 );
    }

    /**
     * Whether sensor permission requests must run inside a user gesture.
     * iOS auto-denies `DeviceOrientationEvent.requestPermission()` outside a
     * gesture, so passive callers (service start, wake retries) must defer to
     * explicit entry points like the current-location FAB.
     * @returns True when the permission API exists on a gesture-gated platform.
     */
    public requiresUserGestureForPermission(): boolean {
        const { hasOrientationAPI, hasMotionAPI } = this.checkPermissionApiAvailable();

        return this.isLikelyIOS() && ( hasOrientationAPI || hasMotionAPI );
    }

    /**
     * Request compass permissions (DeviceOrientationEvent and DeviceMotionEvent).
     * Required on iOS 13+ and on Chromium builds that gate sensors behind Ask.
     * Must preferably run inside a user gesture when the permission API exists.
     * @returns True if sensors may be used (granted, not required, or non-iOS soft allow)
     */
    public async requestPermission(): Promise<boolean> {
        const { hasOrientationAPI, hasMotionAPI } = this.checkPermissionApiAvailable();

        // If no permission API is available, permission is not required
        if ( !hasOrientationAPI && !hasMotionAPI ) {
            this.updatePermissionState( "not_required" );

            return true;
        }

        let orientationGranted = true;
        let motionGranted = true;

        // Request permission for DeviceOrientationEvent
        if ( hasOrientationAPI ) {
            try {
                const orientationResp = await ( DeviceOrientationEvent as unknown as { "requestPermission": () => Promise<string> } ).requestPermission();
                orientationGranted = orientationResp === "granted";
            } catch( error ) {
                console.error( "[CompassPermissionService] DeviceOrientationEvent permission error:", error );
                orientationGranted = false;
            }
        }

        // Request permission for DeviceMotionEvent (for gyroscope)
        if ( hasMotionAPI ) {
            try {
                const motionResp = await ( DeviceMotionEvent as unknown as { "requestPermission": () => Promise<string> } ).requestPermission();
                motionGranted = motionResp === "granted";
            } catch( error ) {
                console.error( "[CompassPermissionService] DeviceMotionEvent permission error:", error );
                motionGranted = false;
            }
        }

        const allGranted = orientationGranted && motionGranted;
        if ( allGranted ) {
            this.updatePermissionState( "granted" );

            return true;
        }

        /*
         * iOS: denied without a gesture is fatal — keep denied.
         * Chromium / Brave: requestPermission() often throws or denies when called from
         * ServicesInitializer (no user activation). Still allow start(); site Sensors
         * settings or a later gesture wake may deliver events.
         */
        if ( this.isLikelyIOS() ) {
            this.updatePermissionState( "denied" );

            return false;
        }

        console.warn( "[CompassPermissionService] Permission API denied/failed outside iOS; continuing with soft allow" );
        this.updatePermissionState( "not_required" );

        return true;
    }

    /**
     * Update permission state and notify listeners
     * @param state - New permission state
     */
    private updatePermissionState( state: CompassPermissionState ): void {
        if ( this.permissionState !== state ) {
            this.permissionState = state;
            this.emitPermissionState( state );
        }
    }

    /**
     * Emit permission state to all listeners
     * @param state - Permission state to emit
     */
    private emitPermissionState( state: CompassPermissionState ): void {
        this.permissionListeners.forEach( ( listener ) => {
            try {
                listener( state );
            } catch( error ) {
                console.error( "[CompassPermissionService] Error in permission listener:", error );
            }
        } );
    }

    /**
     * Get current permission state
     * @returns Current permission state
     */
    public getPermissionState(): CompassPermissionState {
        return this.permissionState;
    }

    /**
     * Check if compass permission is granted
     * @returns True if permission is granted or not required
     */
    public isPermissionGranted(): boolean {
        return this.permissionState === "granted" || this.permissionState === "not_required";
    }

    /**
     * Subscribe to permission state changes
     * @param listener - Callback function that receives permission state
     * @returns Unsubscribe function
     */
    public subscribeToPermissionState( listener: PermissionStateListener ): () => void {
        // Emit current state immediately
        listener( this.permissionState );

        this.permissionListeners.add( listener );

        return () => {
            this.permissionListeners.delete( listener );
        };
    }

    /**
     * Update permission state (called from external services)
     * @param state - New permission state
     */
    public setPermissionState( state: CompassPermissionState ): void {
        this.updatePermissionState( state );
    }

    /**
     * Initialize permission state on service creation
     * Checks if permission API is available and sets state accordingly
     */
    public initialize(): void {
        const { hasOrientationAPI, hasMotionAPI } = this.checkPermissionApiAvailable();

        if ( !hasOrientationAPI && !hasMotionAPI ) {
            this.updatePermissionState( "not_required" );
        }
    }

}

/**
 * Export singleton instance for convenience
 */
export const compassPermissionService = CompassPermissionService.getInstance();
